import { useMemo, useState } from 'react';
import { Button } from '../../design-system';
import { executeCommand, newIdempotencyKey } from '../../lib/command';
import './CommandBar.css';

// The confirmation screen every write passes through.
//
// ⚠️ NOTHING HAS BEEN CREATED AT THIS POINT. /api/ai/command parses and logs;
// it writes no task, no assignment, nothing. This screen is where a human turns
// a reading into an instruction, which is the whole reason the two endpoints
// are separate.
//
// ⚠️ AN AMBIGUOUS SLOT IS A PICKER, NOT A GUESS. The server returns candidates
// rather than choosing when the top two are close, because a confidently wrong
// auto-fill is not recoverable — nobody re-reads a field the machine filled in,
// and one client's return quietly gets filed against another's books.
export default function ConfirmCommand({ parsed, onCancel, onDone }) {
  const [corrections, setCorrections] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // ⚠️ GENERATED ONCE, FOR THE LIFE OF THIS SCREEN. A key regenerated per click
  // would make every double-tap a fresh command, which is exactly what it is
  // there to prevent.
  const idempotencyKey = useMemo(() => newIdempotencyKey(), []);

  const slots = parsed.slots || {};
  // ⚠️ THE SERVER DECIDES WHAT BLOCKS, NOT THIS SCREEN. The model now fills in
  // a dozen optional fields — a priority, a fee, who is assisting — and an
  // unread one of those must not stop a command that named its client and its
  // work. Blocking on "any unresolved slot" meant one unrecognised name sent
  // the user back to retype the whole sentence. `blockingSlots` is the same
  // list /execute enforces, so the button and the endpoint cannot disagree.
  const blocking = (parsed.blockingSlots || [])
    .filter((k) => !corrections[k] && slots[k]?.status !== 'resolved');
  const ready = parsed.canExecute && !blocking.length && parsed.intent !== 'needs_clarification';

  const confirm = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await executeCommand({
        commandLogId: parsed.commandLogId,
        idempotencyKey,
        corrections,
      });
      onDone?.(res.result);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not run that command');
    } finally {
      setBusy(false);
    }
  };

  const pick = (slot, cand) => {
    setCorrections((c) => ({ ...c, [slot]: cand.id }));
    // The chosen candidate is reflected locally so the summary updates; the
    // SERVER still re-validates the id against the candidates it offered.
    slots[slot] = { ...slots[slot], status: 'resolved', value: { id: cand.id, label: cand.label } };
  };

  return (
    <div className="cmd-confirm">
      <div className="cmd-confirm-head">
        <h3 className="cmd-confirm-title">{parsed.preview?.title || 'Confirm'}</h3>
        {/* ⚠️ SAID OUT LOUD, because "did the AI actually do anything?" is
            otherwise unanswerable from the screen — and the answer changes how
            much you trust the reading. `parser` means it was understood locally
            with no network call at all; `llm` means the model was asked to
            segment the sentence first. */}
        <span className={`cmd-tier is-${parsed.resolvedTier}`}>
          {parsed.resolvedTier === 'llm' ? 'read by AI' : 'read locally'}
        </span>
      </div>

      <ul className="cmd-confirm-lines">
        {(parsed.preview?.lines || []).map((l) => <li key={l}>{l}</li>)}
      </ul>

      {Object.entries(slots).map(([slot, s]) => (
        s.status === 'ambiguous' && s.candidates?.length ? (
          <div key={slot} className="cmd-pick">
            <p className="cmd-pick-q">
              Which <b>{slot}</b> did you mean by “{s.text}”?
            </p>
            <div className="cmd-pick-options">
              {s.candidates.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`cmd-pick-option${corrections[slot] === c.id ? ' is-chosen' : ''}`}
                  onClick={() => pick(slot, c)}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        ) : null
      ))}

      {blocking.length > 0 && (
        <p className="cmd-warn">
          {`Still needed: ${blocking.join(', ')}. Use the Fields tab to pick ${blocking.length > 1 ? 'them' : 'it'} directly.`}
        </p>
      )}

      {/* An OPTIONAL field that could not be read is a note, not a warning:
          the task is still going to be created, just without it. */}
      {!blocking.length
        && Object.entries(slots).some(([k, s]) => s.status === 'unresolved' && k !== 'due') && (
        <p className="cmd-note">
          Anything not recognised above will be left blank — you can fill it in on the task.
        </p>
      )}

      {!parsed.canExecute && (
        <p className="cmd-warn">You do not have permission to run this command.</p>
      )}

      {error && <p className="cmd-error">{error}</p>}

      <div className="cmd-confirm-actions">
        <Button variant="ghost" onClick={onCancel} disabled={busy}>Back</Button>
        <Button variant="primary" onClick={confirm} loading={busy} disabled={!ready}>
          Confirm
        </Button>
      </div>
    </div>
  );
}
